import { Skeleton, TableBody } from '@mui/material';
import Table from '@mui/material/Table';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import React from 'react';
import { Aggregation } from 'services/hooks/space-service';
import { Order, getComparator } from 'utils/tableUtils';

export interface PopularityData {
  name: string;
  avgOccupancy: number;
  capacity: number;
}

interface PopularityHeadCell {
  id: keyof PopularityData;
  label: string;
  numeric: boolean;
}

const popularityHeadCells: readonly PopularityHeadCell[] = [
  {
    id: 'name',
    numeric: false,
    label: 'Space'
  },
  {
    id: 'avgOccupancy',
    numeric: true,
    label: 'Avg. Occupants'
  }
];

interface EnhancedPopularityTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof PopularityData) => void;
  order: Order;
  orderBy: string;
}

function EnhancedPopularityTableHead(props: EnhancedPopularityTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: keyof PopularityData) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {popularityHeadCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            sortDirection={orderBy === headCell.id ? order : false}
            style={{ fontWeight: 'bold' }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export function SpacePopularityTable(props: {
  popularityRows: PopularityData[];
  loading: boolean;
  aggregation: Aggregation;
}) {
  const { popularityRows, loading, aggregation } = props;
  const [popularityOrder, setPopularityOrder] = React.useState<Order>('desc');
  const [popularityOrderBy, setPopularityOrderBy] =
    React.useState<keyof PopularityData>('avgOccupancy');

  const handlePopularityColumnSort = (
    _: React.MouseEvent<unknown>,
    property: keyof PopularityData
  ) => {
    const isAsc = popularityOrderBy === property && popularityOrder === 'asc';
    setPopularityOrder(isAsc ? 'desc' : 'asc');
    setPopularityOrderBy(property);
  };

  const sortedPopularityRows = React.useMemo(
    () => popularityRows.slice().sort(getComparator(popularityOrder, popularityOrderBy)),
    [popularityOrder, popularityOrderBy, popularityRows]
  );

  if (loading) {
    return <Skeleton variant="rectangular" height={600} />;
  }

  return (
    <>
      <TableContainer>
        <Table size="small" aria-label="Space popularity">
          <EnhancedPopularityTableHead
            order={popularityOrder}
            orderBy={popularityOrderBy}
            onRequestSort={handlePopularityColumnSort}
          />
          <TableBody>
            {sortedPopularityRows.map((row) => {
              return (
                <TableRow hover tabIndex={-1} key={row.name}>
                  <TableCell component="th" scope="row">
                    {row.name}
                  </TableCell>
                  <TableCell align="right">
                    {row.avgOccupancy.toLocaleString()} {aggregation}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
